import React, { useState } from 'react';

/**
 * The deposit banner on a request, and the front desk's only route to the
 * deposit's Stripe invoice.
 *
 * Replaces two hand-copied blocks in requestimateState.jsx (the pre-received
 * banner and the RECEIVED one) that had already drifted apart in their copy.
 *
 * WHEN THERE IS NO INVOICE: for most of a deposit's life there deliberately
 * isn't one. `DepositModal` runs in two modes — pre-booking it only writes
 * `deposit_amount` to the request ("Save Deposit Amount"), and the Stripe
 * invoice is not minted until Book Job. So an estimate carrying a deposit shows
 * the amount with nothing behind it, and that is normal, not a fault. Rarer:
 * a legacy deposit written before the client recorded payment rows, or one
 * whose invoice was created but whose row write failed
 * (requestimates.jsx:1067). None of them can produce a link, so none of them
 * get a button — a dead click here costs the customer's time at the counter.
 *
 * `depositStatus` is the availability signal because it rides on the request
 * itself. It counts VOIDED deposit rows too (the server derives it with
 * COUNT(*) > 0), so it can promise a link that the click cannot deliver; the
 * handler says so plainly rather than failing silently.
 */
const DepositAlert = ({ request, onOpenInvoice, blockedNote = false }) => {
  const [opening, setOpening] = useState(false);

  const amount = parseFloat(request.depositAmount || 0);
  if (!(amount > 0) || request.requestType !== 'installer') return null;

  const isPaid = !!request.depositIsPaid;
  const status = request.depositStatus;
  const hasInvoice = !!status && status !== 'none';

  const handleOpen = async () => {
    if (opening || !onOpenInvoice) return;
    setOpening(true);
    try {
      await onOpenInvoice(request);
    } finally {
      setOpening(false);
    }
  };

  return (
    <div className={`alert ${isPaid ? 'alert-success' : 'alert-warning'} mx-3 mb-3`}>
      <strong>Deposit Required:</strong> ${amount.toFixed(2)}
      <div>
        <span className={`d-block ${isPaid ? 'text-success' : 'text-warning'}`}>
          {isPaid
            ? <><i className="fa-sharp fa-regular fa-check mr-1"></i> Paid</>
            : <>
                <i className="fa-sharp fa-regular fa-clock mr-1"></i> Not Paid
                {blockedNote && ' — work can\'t start until the deposit is paid'}
              </>
          }
        </span>
        {/* No "$X received" line: a deposit invoice is paid or it isn't, so the
            amount adds nothing the banner hasn't already said. */}
        {hasInvoice ? (
          // Its own block so the link sits UNDER the paid/unpaid status rather
          // than trailing it on the same line.
          <div>
            <button
              type="button"
              className="btn btn-link btn-sm pl-0 mt-1"
              onClick={handleOpen}
              disabled={opening}
            >
              {opening
                ? <><i className="fa-sharp fa-regular fa-spinner fa-spin mr-1"></i> Opening…</>
                : <><i className="fa-sharp fa-regular fa-file-invoice-dollar mr-1"></i> Open Invoice</>
              }
            </button>
          </div>
        ) : (
          <small className="d-block text-muted mt-1">
            No deposit invoice yet — it is created when the job is booked.
          </small>
        )}
      </div>
    </div>
  );
};

export default DepositAlert;
